import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, FileText, Receipt, CheckCircle2, Save, FileSignature, Pencil, Send } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { JOB_STATUSES, CURRENCIES, PIECES_UNITS, currencySymbol } from "@/lib/constants";
import PodUpload from "@/components/PodUpload";
import RateConfDialog from "@/components/RateConfDialog";
import JobFormDialog from "@/components/JobFormDialog";

/**
 * Address card with inline date/appointment editing — pickup/delivery
 * gets its own date fields directly on the card (no more separate sidebar).
 */
const AddrCard = ({ label, a, dateLabel, dateValue, onDateChange, apptValue, onApptChange, apptLabel = "Appointment" }) => (
  <div className="border border-zinc-200 p-4 bg-white space-y-3">
    <div>
      <div className="mono-label">{label}</div>
      <div className="mt-2 text-sm">
        {a?.name && <div className="font-medium">{a.name}</div>}
        <div>{a?.street}</div>
        <div>{a?.city}, {a?.state} {a?.zip}</div>
        {a?.contact_name && <div className="text-zinc-500 text-xs mt-1">Contact: {a.contact_name} {a.contact_phone && `· ${a.contact_phone}`}</div>}
      </div>
    </div>
    <div className="grid grid-cols-2 gap-2 pt-3 border-t border-zinc-100">
      <div>
        <label className="mono-label">{dateLabel}</label>
        <Input
          type="datetime-local"
          value={dateValue?.slice(0, 16) || ""}
          onChange={(e) => onDateChange(e.target.value)}
          className="sharp mt-1 apex-input h-9"
          data-testid={`${label.toLowerCase()}-date-inline`}
        />
      </div>
      {onApptChange && (
        <div>
          <label className="mono-label">{apptLabel}</label>
          <Input
            type="datetime-local"
            value={apptValue?.slice(0, 16) || ""}
            onChange={(e) => onApptChange(e.target.value)}
            className="sharp mt-1 apex-input h-9"
            data-testid={`${label.toLowerCase()}-appt-inline`}
          />
        </div>
      )}
    </div>
  </div>
);

export default function JobDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [job, setJob] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editable, setEditable] = useState({});
  const [customers, setCustomers] = useState([]);
  const [rateConfs, setRateConfs] = useState([]);
  const [rcOpen, setRcOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const load = async () => {
    try {
      const [{ data }, { data: cust }, { data: rcs }] = await Promise.all([
        api.get(`/jobs/${id}`),
        api.get("/customers"),
        api.get(`/jobs/${id}/rate-confirmations`),
      ]);
      setJob(data);
      setCustomers(cust);
      setRateConfs(rcs);
      setEditable({
        status: data.status,
        driver_name: data.driver_name || "",
        driver_phone: data.driver_phone || "",
        truck_number: data.truck_number || "",
        trailer_number: data.trailer_number || "",
        pickup_date: data.pickup_date || "",
        appointment_date: data.appointment_date || "",
        delivery_date: data.delivery_date || "",
        po_number: data.po_number || "",
        rate: data.rate || "",
        currency: data.currency || "USD",
        bill_to_customer_id: data.bill_to_customer_id || "",
        tracking_url: data.tracking_url || "",
        notes: data.notes || "",
      });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  useEffect(() => { load(); }, [id]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...editable };
      if (payload.rate === "") delete payload.rate;
      else payload.rate = Number(payload.rate);
      if (!payload.bill_to_customer_id) delete payload.bill_to_customer_id;
      const { data } = await api.patch(`/jobs/${id}`, payload);
      setJob(data);
      toast.success("Job updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const generateBol = async () => {
    const win = window.open("", "_blank");
    try {
      const { data } = await api.post(`/jobs/${id}/bol`);
      setJob(data);
      toast.success(`BOL ${data.bol_number} generated`);
      if (win) win.location.href = `/documents/bol/${id}`;
    } catch (err) {
      if (win) win.close();
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const generateInvoice = async () => {
    if (!job.rate) {
      toast.error("Set a client rate before generating an invoice.");
      return;
    }
    const win = window.open("", "_blank");
    try {
      const { data } = await api.post("/invoices", { job_id: id, lines: [] });
      toast.success(`Invoice ${data.invoice_number} generated`);
      if (win) win.location.href = `/documents/invoice/${data.id}`;
      load();
    } catch (err) {
      if (win) win.close();
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (!job) return <div className="mono-label">Loading job...</div>;

  const clientCurrency = editable.currency || job.currency || "USD";
  const clientRate = Number(editable.rate || 0);

  // Sum carrier pay across all rate confirmations on this job (agreed_rate + accessorials).
  const carrierBreakdown = rateConfs.map((rc) => {
    const acc = (rc.accessorials || []).reduce((s, a) => s + (Number(a.amount) || 0), 0);
    const total = (Number(rc.agreed_rate) || 0) + acc;
    return { rc, acc, total };
  });
  const carrierTotal = carrierBreakdown.reduce((s, r) => s + r.total, 0);
  const carrierCurrency = rateConfs[0]?.currency || clientCurrency;
  const margin = clientRate - carrierTotal;

  return (
    <div>
      {/* HEADER */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <button onClick={() => nav(-1)} className="mono-label inline-flex items-center hover:text-zinc-950" data-testid="job-back">
            <ArrowLeft className="h-3 w-3 mr-1" /> BACK
          </button>
          <div className="flex items-center gap-4 mt-2">
            <h1 className="text-3xl font-bold tracking-tight font-mono">{job.job_number}</h1>
            <StatusBadge status={job.status} />
          </div>
          <div className="mono-label mt-1">{job.customer_company || job.customer_name}</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setEditOpen(true)} className="sharp border-zinc-950" data-testid="btn-edit-job">
            <Pencil className="h-4 w-4 mr-2" /> Edit job
          </Button>
          <Button variant="outline" onClick={() => setRcOpen(true)} className="sharp border-zinc-300" data-testid="btn-generate-rc">
            <FileSignature className="h-4 w-4 mr-2" /> New rate confirmation
          </Button>
          <Button variant="outline" onClick={generateBol} className="sharp border-zinc-300" data-testid="btn-generate-bol">
            <FileText className="h-4 w-4 mr-2" />
            {job.bol_number ? `View BOL ${job.bol_number}` : "Generate BOL"}
          </Button>
          <Button onClick={generateInvoice} className="sharp bg-zinc-950 hover:bg-zinc-800" data-testid="btn-generate-invoice">
            <Receipt className="h-4 w-4 mr-2" />
            {job.invoice_number ? `View Invoice ${job.invoice_number}` : "Generate Invoice"}
          </Button>
        </div>
      </div>

      {job.bol_number && (
        <div className="mb-6 bg-emerald-50 border border-emerald-200 p-3 flex items-center gap-2 text-sm">
          <CheckCircle2 className="h-4 w-4 text-emerald-700" />
          BOL <span className="font-mono">{job.bol_number}</span> generated
          <Link to={`/documents/bol/${job.id}`} target="_blank" className="ml-auto text-emerald-800 underline">Open</Link>
        </div>
      )}

      <div className="space-y-4">
        {/* PICKUP + DELIVERY with inline dates */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <AddrCard
            label="PICKUP"
            a={job.pickup}
            dateLabel="Pickup date"
            dateValue={editable.pickup_date}
            onDateChange={(v) => setEditable({ ...editable, pickup_date: v })}
          />
          <AddrCard
            label="DELIVERY"
            a={job.delivery}
            dateLabel="Delivery date"
            dateValue={editable.delivery_date}
            onDateChange={(v) => setEditable({ ...editable, delivery_date: v })}
            apptValue={editable.appointment_date}
            onApptChange={(v) => setEditable({ ...editable, appointment_date: v })}
          />
        </div>

        {/* FREIGHT DETAILS (no rate — rate lives in its own section below) */}
        <div className="border border-zinc-200 p-5 bg-white">
          <div className="flex items-center justify-between">
            <div className="mono-label">FREIGHT DETAILS</div>
            <div className="text-right">
              <span className="mono-label">Bill to</span>
              <div className="text-sm font-medium mt-0.5" data-testid="job-billto-display">
                {job.bill_to_company || job.bill_to_name || job.customer_company || job.customer_name}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3 text-sm">
            <div><span className="mono-label block">Service</span><span className="font-mono">{job.service_type}</span></div>
            <div><span className="mono-label block">Equipment</span><span className="font-mono">{job.equipment}</span></div>
            <div><span className="mono-label block">Commodity</span>{job.commodity || "—"}</div>
            <div><span className="mono-label block">Weight</span><span className="font-mono">{job.weight ? `${job.weight} lb` : "—"}</span></div>
            <div><span className="mono-label block">Pieces</span><span className="font-mono">{job.pieces ? `${job.pieces} ${job.pieces_unit || ""}` : "—"}</span></div>
            <div><span className="mono-label block">Reference</span><span className="font-mono">{job.reference || "—"}</span></div>
            <div>
              <span className="mono-label block">PO #</span>
              <Input
                value={editable.po_number}
                onChange={(e) => setEditable({ ...editable, po_number: e.target.value })}
                className="sharp mt-1 apex-input font-mono h-9"
                data-testid="detail-po"
              />
            </div>
            <div>
              <span className="mono-label block">Status</span>
              <Select value={editable.status} onValueChange={(v) => setEditable({ ...editable, status: v })}>
                <SelectTrigger className="sharp mt-1 h-9" data-testid="detail-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {JOB_STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {Array.isArray(job.containers) && job.containers.length > 0 && (
            <div className="mt-4 border border-zinc-950" data-testid="job-containers">
              <div className="bg-zinc-950 text-white px-3 py-1.5 mono-label !text-zinc-100">
                CONTAINERS &middot; {job.containers.length}
              </div>
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 border-b border-zinc-200">
                  <tr>
                    <th className="text-left py-1.5 px-3 mono-label">Container #</th>
                    <th className="text-left py-1.5 px-3 mono-label">Pickup date</th>
                    <th className="text-left py-1.5 px-3 mono-label">Drop date</th>
                    <th className="text-left py-1.5 px-3 mono-label">In-gate date</th>
                  </tr>
                </thead>
                <tbody>
                  {job.containers.map((c, i) => (
                    <tr key={i} className={i % 2 ? "bg-zinc-50" : ""}>
                      <td className="py-1.5 px-3 font-mono font-semibold">{c.container_number || "—"}</td>
                      <td className="py-1.5 px-3 font-mono">{c.pickup_date || "—"}</td>
                      <td className="py-1.5 px-3 font-mono">{c.drop_date || "—"}</td>
                      <td className="py-1.5 px-3 font-mono">{c.ingate_date || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* CLIENT RATE + CARRIER RATE side-by-side */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* CLIENT RATE (what we charge the customer) */}
          <div className="border border-emerald-300 bg-emerald-50 p-5" data-testid="client-rate-section">
            <div className="flex items-center justify-between mb-3">
              <div className="mono-label !text-emerald-900">CLIENT RATE (BILLED)</div>
              <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 bg-white border border-emerald-300 text-emerald-800">
                → {job.bill_to_company || job.customer_company || "customer"}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2">
                <label className="mono-label">Rate</label>
                <Input
                  type="number"
                  step="0.01"
                  value={editable.rate}
                  onChange={(e) => setEditable({ ...editable, rate: e.target.value })}
                  className="sharp mt-1 apex-input font-mono text-lg bg-white"
                  data-testid="detail-rate"
                />
              </div>
              <div>
                <label className="mono-label">Currency</label>
                <Select value={editable.currency} onValueChange={(v) => setEditable({ ...editable, currency: v })}>
                  <SelectTrigger className="sharp mt-1 h-10 bg-white" data-testid="detail-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((c) => <SelectItem key={c.key} value={c.key}>{c.key}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-emerald-200 flex items-center justify-between text-sm">
              <span className="mono-label !text-emerald-900">Total billed</span>
              <span className="font-mono font-bold text-emerald-900 text-lg">
                {currencySymbol(clientCurrency)}{clientRate.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {clientCurrency}
              </span>
            </div>
          </div>

          {/* CARRIER RATE (what we pay the carrier — sum of RCs) */}
          <div className="border border-rose-300 bg-rose-50 p-5" data-testid="carrier-rate-section">
            <div className="flex items-center justify-between mb-3">
              <div className="mono-label !text-rose-900">CARRIER RATE (PAYABLE)</div>
              <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 bg-white border border-rose-300 text-rose-800">
                ← {rateConfs.length} RC{rateConfs.length !== 1 ? "s" : ""}
              </span>
            </div>
            {rateConfs.length === 0 ? (
              <div className="text-sm text-zinc-600 py-2">
                No carrier assigned yet. Create a rate confirmation to lock the carrier pay.
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setRcOpen(true)}
                  className="sharp mt-2 h-8 border-rose-400 bg-white hover:bg-rose-100 block"
                  data-testid="carrier-rate-new-rc-btn"
                >
                  <FileSignature className="h-3.5 w-3.5 mr-1" /> New rate confirmation
                </Button>
              </div>
            ) : (
              <div className="space-y-2 text-sm">
                {carrierBreakdown.map(({ rc, acc, total }) => (
                  <div key={rc.id} className="flex items-center justify-between border border-rose-200 bg-white px-3 py-2" data-testid={`carrier-line-${rc.rc_number}`}>
                    <div>
                      <span className="font-mono font-semibold">{rc.rc_number}</span>
                      <span className="ml-2 text-zinc-600 text-xs">{rc.carrier_name}</span>
                    </div>
                    <div className="text-right">
                      <div className="font-mono">
                        {currencySymbol(rc.currency)}{Number(rc.agreed_rate).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        {acc > 0 && (
                          <span className="text-xs text-zinc-500 ml-1">+ {currencySymbol(rc.currency)}{acc.toFixed(2)} acc</span>
                        )}
                      </div>
                      <div className="text-[10px] mono-label !text-rose-800">Total: {currencySymbol(rc.currency)}{total.toFixed(2)}</div>
                    </div>
                  </div>
                ))}
                <div className="pt-2 border-t border-rose-200 flex items-center justify-between">
                  <span className="mono-label !text-rose-900">Total payable</span>
                  <span className="font-mono font-bold text-rose-900 text-lg">
                    {currencySymbol(carrierCurrency)}{carrierTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {carrierCurrency}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* MARGIN — quick visual (only when both rates exist) */}
        {clientRate > 0 && carrierTotal > 0 && clientCurrency === carrierCurrency && (
          <div className={`border p-3 flex items-center justify-between text-sm ${margin >= 0 ? "border-zinc-950 bg-zinc-950 text-white" : "border-rose-500 bg-rose-100 text-rose-900"}`} data-testid="margin-strip">
            <span className="mono-label !text-current opacity-80">MARGIN (CLIENT − CARRIER)</span>
            <span className="font-mono font-bold text-lg">
              {margin >= 0 ? "" : "-"}{currencySymbol(clientCurrency)}{Math.abs(margin).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              <span className="ml-3 text-xs opacity-70">
                ({clientRate ? ((margin / clientRate) * 100).toFixed(1) : "0"}%)
              </span>
            </span>
          </div>
        )}

        {/* DRIVER STRIP — compact single row, no more sidebar */}
        <div className="border border-zinc-200 p-4 bg-white">
          <div className="flex items-center justify-between mb-3">
            <div className="mono-label">DRIVER &amp; TRACKING</div>
            <Button
              onClick={save}
              disabled={saving}
              size="sm"
              className="sharp bg-[#0056B3] hover:bg-[#004494]"
              data-testid="detail-save"
            >
              <Save className="h-3.5 w-3.5 mr-1" /> {saving ? "Saving..." : "Save changes"}
            </Button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div>
              <label className="mono-label">Driver name</label>
              <Input
                value={editable.driver_name}
                onChange={(e) => setEditable({ ...editable, driver_name: e.target.value })}
                className="sharp mt-1 apex-input h-9"
                data-testid="detail-driver"
              />
            </div>
            <div>
              <label className="mono-label">Driver phone</label>
              <Input
                value={editable.driver_phone}
                onChange={(e) => setEditable({ ...editable, driver_phone: e.target.value })}
                className="sharp mt-1 apex-input h-9"
              />
            </div>
            <div>
              <label className="mono-label">Truck #</label>
              <Input
                value={editable.truck_number}
                onChange={(e) => setEditable({ ...editable, truck_number: e.target.value })}
                className="sharp mt-1 apex-input font-mono h-9"
              />
            </div>
            <div>
              <label className="mono-label">Trailer #</label>
              <Input
                value={editable.trailer_number}
                onChange={(e) => setEditable({ ...editable, trailer_number: e.target.value })}
                className="sharp mt-1 apex-input font-mono h-9"
              />
            </div>
            <div>
              <label className="mono-label">Bill to (override)</label>
              <Select
                value={editable.bill_to_customer_id || job.customer_id}
                onValueChange={(v) => setEditable({ ...editable, bill_to_customer_id: v })}
              >
                <SelectTrigger className="sharp mt-1 h-9" data-testid="detail-billto"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.company || c.name} {c.company ? `(${c.name})` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 md:col-span-5">
              <label className="mono-label">Tracking link (visible to customer)</label>
              <Input
                value={editable.tracking_url}
                onChange={(e) => setEditable({ ...editable, tracking_url: e.target.value })}
                placeholder="https://carrier-portal.com/track/ABC123"
                className="sharp mt-1 apex-input h-9"
                data-testid="detail-tracking-url"
              />
              {job.tracking_url && (
                <a href={job.tracking_url} target="_blank" rel="noreferrer" className="text-[#0056B3] underline font-mono text-xs mt-1 inline-block break-all" data-testid="job-tracking-link">
                  {job.tracking_url}
                </a>
              )}
            </div>
          </div>
        </div>

        {/* RATE CONFIRMATIONS list */}
        {rateConfs.length > 0 && (
          <div className="border border-zinc-200 p-5 bg-white">
            <div className="mono-label mb-3">RATE CONFIRMATIONS</div>
            <div className="space-y-2">
              {rateConfs.map((rc) => {
                const alreadySent = !!rc.email_sent_at;
                const sendRc = async () => {
                  if (!rc.carrier_contact_email && !window.confirm("No carrier email on file for this RC. Enter one now?")) {
                    return;
                  }
                  let to_email = rc.carrier_contact_email;
                  if (!to_email) {
                    to_email = window.prompt("Carrier email:");
                    if (!to_email) return;
                  }
                  if (alreadySent && !window.confirm(`Send a REVISED confirmation to ${to_email}?\n\nThis will supersede any previous copy.`)) {
                    return;
                  }
                  try {
                    const { data } = await api.post(`/rate-confirmations/${rc.id}/send-email`, { to_email });
                    toast.success(`${data.revised ? "Revised c" : "C"}onfirmation sent to ${data.sent_to}`);
                    load();
                  } catch (err) {
                    toast.error(formatApiError(err.response?.data?.detail));
                  }
                };
                return (
                  <div key={rc.id} className="flex items-center justify-between border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm" data-testid={`rc-row-${rc.rc_number}`}>
                    <div>
                      <span className="font-mono font-semibold">{rc.rc_number}</span>
                      <span className="ml-3 text-zinc-600">{rc.carrier_name} · MC <span className="font-mono">{rc.mc_number}</span></span>
                      {alreadySent && (
                        <span className="ml-3 inline-block px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider bg-emerald-100 text-emerald-800 border border-emerald-300">
                          SENT{rc.revision_count ? ` · rev ${rc.revision_count}` : ""}
                        </span>
                      )}
                      {Array.isArray(rc.accessorials) && rc.accessorials.length > 0 && (
                        <span className="ml-2 inline-block px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-300">
                          +{rc.accessorials.length} ACCESSORIAL
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-mono">{currencySymbol(rc.currency)}{Number(rc.agreed_rate).toLocaleString()} {rc.currency}</span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={sendRc}
                        className="sharp h-8 border-zinc-300"
                        data-testid={`rc-send-${rc.rc_number}`}
                      >
                        <Send className="h-3.5 w-3.5 mr-1" />
                        {alreadySent ? "Send revised" : "Send"}
                      </Button>
                      <Link to={`/documents/rate-conf/${rc.id}`} target="_blank" className="text-[#0056B3] underline">Open</Link>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <PodUpload jobId={job.id} files={job.pod_files || []} onChange={setJob} />

        <div className="border border-zinc-200 p-5 bg-white">
          <div className="mono-label mb-3">NOTES</div>
          <Textarea
            rows={3}
            value={editable.notes}
            onChange={(e) => setEditable({ ...editable, notes: e.target.value })}
            className="sharp apex-input"
            placeholder="Add notes for this job..."
            data-testid="detail-notes"
          />
        </div>
      </div>

      <RateConfDialog
        open={rcOpen}
        onOpenChange={setRcOpen}
        jobId={job.id}
        defaultRate={job.rate}
        defaultCurrency={job.currency}
        defaultAccessorials={job.accessorials || []}
        onCreated={(rc) => {
          load();
          window.open(`/documents/rate-conf/${rc.id}`, "_blank");
        }}
      />

      <JobFormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        asAdmin
        job={job}
        customers={customers}
        onCreated={(updated) => {
          setJob(updated);
          load();
        }}
      />
    </div>
  );
}
